/**
 * Framework-agnostic helpers shared across the Cleaner Activity tab.
 * Single source of truth for complaint/SLA classification, time helpers, and formatting.
 */

/**
 * @param {Date|string|number|null|undefined} value
 * @returns {number}
 */
export function toTimestamp(value) {
  if (!value) return NaN
  if (value instanceof Date) return value.getTime()
  return new Date(value).getTime()
}

/**
 * Calendar date of a Date as 'YYYY-MM-DD', read in local time. The date picker hands back local
 * Date objects that have to be compared against the company-local date strings used by the API.
 * @param {Date} date
 * @returns {string} empty when the value is not a usable date
 */
export function toLocalDateStr(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * A row is a genuine complaint only when it was closed by a different servik than it was
 * opened with. When open/close servik match, the cleaner just performed the action (no real
 * open→resolve cycle), so response-time / SLA framing doesn't apply.
 * @param {Object} complaint
 * @returns {boolean}
 */
export function isGenuineComplaint(complaint) {
  return (
    !!complaint
    && complaint.closeServikId != null
    && Number(complaint.closeServikId) !== Number(complaint.servikId)
  )
}

/**
 * A complaint breaches SLA when it was auto-expired (closed past budget) or, while still
 * open, has been open longer than the shift's max SLA. Never a breach when no SLA is configured.
 * @param {Object} complaint
 * @param {number|string|null} slaMinutes shift max SLA in minutes
 * @param {number} [nowMs] upper bound for still-open complaints (defaults to now)
 * @returns {boolean}
 */
export function isComplaintBreach(complaint, slaMinutes, nowMs = Date.now()) {
  if (!complaint) return false
  if (complaint.closeTime) {
    if (!isGenuineComplaint(complaint)) return false
    return !!complaint.isExpired
  }
  const maxSlaMs = Number(slaMinutes) > 0 ? Number(slaMinutes) * 60 * 1000 : null
  if (maxSlaMs == null) return false
  const open = toTimestamp(complaint.openTime)
  if (Number.isNaN(open)) return false
  return nowMs - open > maxSlaMs
}

/**
 * Status dot state for a cleaner: `out` (not clocked in), `in` (on time), or `late`
 * (currently clocked in but first punch today was after shift start).
 * @param {Object} cleaner
 * @param {Date|string|null} [shiftStart]
 * @returns {'out'|'in'|'late'}
 */
export function getCleanerClockDotStatus(cleaner, shiftStart = null) {
  const sessions = (cleaner.sessions || []).filter((session) => session.clockIn)
  const isClockedIn =
    cleaner.metrics?.isClockedIn ??
    sessions.some((session) => session.clockIn && !session.clockOut)
  if (!isClockedIn) return 'out'

  const shiftStartMs = toTimestamp(shiftStart)
  if (!shiftStart || Number.isNaN(shiftStartMs)) return 'in'

  const firstClockInMs = sessions
    .map((session) => toTimestamp(session.clockIn))
    .filter((ms) => !Number.isNaN(ms))
    .reduce((min, ms) => Math.min(min, ms), Infinity)

  if (!Number.isFinite(firstClockInMs)) return 'in'
  return firstClockInMs > shiftStartMs ? 'late' : 'in'
}

/**
 * Idle time since the cleaner's last closed complaint, or since clock-in when none exist.
 * @param {Object} cleaner
 * @param {number} [nowMs]
 * @returns {number}
 */
export function computeIdleMs(cleaner, nowMs = Date.now()) {
  const complaints = cleaner.complaints || []
  const sessions = cleaner.sessions || []
  let lastActivityMs = NaN

  complaints.forEach((complaint) => {
    if (!complaint.closeTime) return
    const closeMs = toTimestamp(complaint.closeTime)
    if (!Number.isNaN(closeMs)) {
      lastActivityMs = Number.isFinite(lastActivityMs) ? Math.max(lastActivityMs, closeMs) : closeMs
    }
  })

  if (!Number.isFinite(lastActivityMs)) {
    const openSessions = sessions.filter((session) => session.clockIn && !session.clockOut)
    if (openSessions.length) {
      const clockInMs = openSessions
        .map((session) => toTimestamp(session.clockIn))
        .filter((ms) => !Number.isNaN(ms))
        .reduce((min, ms) => Math.min(min, ms), Infinity)
      if (Number.isFinite(clockInMs)) lastActivityMs = clockInMs
    }
  }

  if (!Number.isFinite(lastActivityMs)) return 0
  return Math.max(0, nowMs - lastActivityMs)
}

/**
 * Complaints the cleaner deferred: a newer complaint in another room was closed before this one.
 * @param {Object} cleaner
 * @param {(roomId: number) => string} roomNameFn
 * @param {number} [nowMs]
 * @returns {Array<{complaint:Object, roomId:Number, roomNames:String[], count:Number, isOpen:Boolean}>}
 */
export function deferredComplaints(cleaner, roomNameFn, nowMs = Date.now()) {
  const complaints = (cleaner && cleaner.complaints) || []
  const result = []

  complaints.forEach((complaint) => {
    const openMs = toTimestamp(complaint.openTime)
    if (Number.isNaN(openMs)) return
    const isOpen = !complaint.closeTime
    const endMs = isOpen ? nowMs : toTimestamp(complaint.closeTime)
    if (Number.isNaN(endMs)) return
    const roomId = Number(complaint.roomId)
    const otherRooms = new Set()

    complaints.forEach((other) => {
      if (other === complaint || !other.openTime || !other.closeTime) return
      if (Number(other.roomId) === roomId) return
      const otherOpenMs = toTimestamp(other.openTime)
      const closeMs = toTimestamp(other.closeTime)
      if (
        !Number.isNaN(otherOpenMs)
        && !Number.isNaN(closeMs)
        && otherOpenMs > openMs
        && closeMs >= otherOpenMs
        && closeMs < endMs
      ) {
        otherRooms.add(Number(other.roomId))
      }
    })

    if (otherRooms.size > 0) {
      result.push({
        complaint,
        roomId,
        roomNames: [...otherRooms].map((id) => roomNameFn(id)),
        count: otherRooms.size,
        isOpen,
      })
    }
  })

  return result
}

/**
 * Minutes as a compact human label: "0m", "45m", "1h 20m" (or "1h20m" when compact).
 * @param {number|string} minutes
 * @param {{ compact?: boolean }} [options]
 * @returns {string}
 */
export function formatMinutes(minutes, { compact = false } = {}) {
  const total = Number(minutes)
  if (!Number.isFinite(total) || total <= 0) return '0m'
  const hours = Math.floor(total / 60)
  const mins = Math.round(total % 60)
  const sep = compact ? '' : ' '
  if (hours && mins) return `${hours}h${sep}${mins}m`
  if (hours) return `${hours}h`
  return `${mins}m`
}

/** Human-readable duration from ms: "45s" / "12m" / "1h 20m". */
export function formatDurationMs(ms) {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return ''
  const totalSeconds = Math.round(ms / 1000)
  if (totalSeconds < 60) return `${totalSeconds}s`
  const totalMinutes = Math.floor(totalSeconds / 60)
  if (totalMinutes < 60) return `${totalMinutes}m`
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`
}

/** Transit gap formatted at minute resolution. Negatives clamp to 0m. */
export function formatTransitMs(ms) {
  const safe = Number.isFinite(ms) ? Math.max(0, ms) : 0
  return formatMinutes(Math.round(safe / 60000))
}

/** Fallback when the current shift has no valid clean_time. */
export const DEFAULT_IDLE_THRESHOLD_MINUTES = 15

/**
 * Idle threshold for the attention strip: shift clean_time (minutes per visit), or fallback.
 * @param {number|string|null|undefined} cleanTimeMinutes shift.clean_time
 * @returns {number}
 */
export function resolveIdleThresholdMinutes(cleanTimeMinutes) {
  const n = Number(cleanTimeMinutes)
  if (Number.isFinite(n) && n > 0) return n
  return DEFAULT_IDLE_THRESHOLD_MINUTES
}
